import type { ColDef, ValueFormatterParams, ValueParserParams } from "@ag-grid-community/core";
import { t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { KeyNames, noop } from "@regrello/core-utils";
import {
  type FieldInstanceFields,
  FormConstraintConditionOperator,
  type FormFieldFields,
  PropertyDataType,
  type PropertyTypeFields,
  type SpectrumFieldValidationTypeFields,
  type SpectrumValueConstraintFields,
} from "@regrello/graphql-api";
import { RegrelloTooltip } from "@regrello/ui-core";
import type { ReactNode } from "react";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

import { getPartyFullName } from "../../../../../utils/parties/partyUtils";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../views/modals/formDialogs/spectrumFields/_internal/ConfigureSpectrumFieldForm";
import type { CustomFieldPlugin } from "../../../customFields/plugins/types/CustomFieldPlugin";
import { RegrelloFormFieldSelectV2 } from "../../../formFields/RegrelloFormFieldSelectV2";
import { RegrelloFormFieldSwitch } from "../../../formFields/RegrelloFormFieldSwitch";
import {
  type ProspectiveSpectrumFieldVersion,
  SpectrumFieldPluginDecorator,
} from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import {
  ALLOW_MULTIPLE_PARTY_ARG,
  DISALLOW_MULTIPLE_PARTY_ARG,
  FrontendValueConstraintRuleName,
  getValueConstraintDisplayName,
} from "../../utils/spectrumFieldConstraintUtils";
import { RegrelloEditableCellPartySelect } from "./_internal/RegrelloEditableCellPartySelect";
import type { UserFieldPluginFrontendValue } from "./_internal/types";

export class SpectrumUserFieldPluginDecorator<
  T extends UserFieldPluginFrontendValue,
> extends SpectrumFieldPluginDecorator<T> {
  constructor(plugin: CustomFieldPlugin<T>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.user";
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.PARTY;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === PropertyDataType.PARTY_ID);
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.PARTY,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(valueConstraints: SpectrumValueConstraintFields[]) {
    return valueConstraints.filter(
      ({ valueConstraintRule }) => valueConstraintRule === FrontendValueConstraintRuleName.MAX_PARTY,
    );
  }

  public getConstraintConditionOperators() {
    return {
      [FormConstraintConditionOperator.ARRAY_EMPTY]: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.ARRAY_NOT_EMPTY]: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.ARRAY_EQUALS]: {
        label: t`Is equal to`,
        inputCount: 1,
        isMultiselect: true,
      },
      [FormConstraintConditionOperator.ARRAY_NOT_EQUALS]: {
        label: t`Is not equal to`,
        inputCount: 1,
        isMultiselect: true,
      },
      [FormConstraintConditionOperator.ARRAY_CONTAINS_ONE_OF]: {
        label: t`Contains any of`,
        inputCount: 1,
        isMultiselect: true,
      },
      [FormConstraintConditionOperator.ARRAY_CONTAINS_ALL]: {
        label: t`Contains all of`,
        inputCount: 1,
        isMultiselect: true,
      },
      [FormConstraintConditionOperator.ARRAY_NOT_CONTAINS_ONE_OF]: {
        label: t`Contains none of`,
        inputCount: 1,
        isMultiselect: true,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onBlur" as const;
  }

  public renderPreviewFormField() {
    return (
      <RegrelloFormFieldSelectV2
        disabled={true}
        getOptionLabel={(option) => option}
        onChange={noop}
        options={[]}
        size="medium"
        value=""
      />
    );
  }

  public renderValueConstraints(props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
    suppressMultiplicityWithExplanation?: string;
    updateConstraint: (index: number, value: ConfigureSpectrumFieldFormFormFields["valueConstraints"][number]) => void;
  }): ReactNode {
    const { constraints, disabled, form, suppressMultiplicityWithExplanation, updateConstraint } = props;
    if (constraints.length === 0) {
      return null;
    }
    const getFieldKey = (index: number) => `valueConstraints.${index}.args.0` as const;
    return (
      <RegrelloTooltip
        content={suppressMultiplicityWithExplanation}
        disabled={suppressMultiplicityWithExplanation == null}
      >
        <div>
          <RegrelloFormFieldSwitch
            checked={form.getValues(getFieldKey(0)) === ALLOW_MULTIPLE_PARTY_ARG}
            disabled={disabled || suppressMultiplicityWithExplanation != null}
            onChange={(isChecked) => {
              const previousValue = form.getValues("valueConstraints.0");
              if (!isChecked) {
                updateConstraint(0, { ...previousValue, args: [DISALLOW_MULTIPLE_PARTY_ARG] });
              } else {
                updateConstraint(0, { ...previousValue, args: [ALLOW_MULTIPLE_PARTY_ARG] });
              }
            }}
            secondaryLabel={getValueConstraintDisplayName(constraints[0].constraint)}
            variant="spectrum"
          />
        </div>
      </RegrelloTooltip>
    );
  }

  public getColumnDef<RowDatum, T>(
    fieldInstance: FieldInstanceFields,
    _formField: FormFieldFields,
    colDefParams: Partial<ColDef<RowDatum, T>>,
  ): ColDef<RowDatum, T> {
    return {
      ...colDefParams,
      autoHeight: true,
      cellClass: "regrello-center-aligned-cell",
      cellEditor: RegrelloEditableCellPartySelect,
      cellEditorParams: {
        fieldInstance,
      },
      cellEditorPopup: true,
      cellEditorPopupPosition: "over", // eslint-disable-line lingui/no-unlocalized-strings
      suppressKeyboardEvent: (params) => {
        // Submit on Cmd + Enter, but not on Enter, as that's the hotkey for selecting and
        // deselecting values in the multi-select input we're using.
        if (params.editing && params.event.key === KeyNames.ENTER && !params.event.metaKey) {
          return true;
        }
        return false;
      },
      suppressPaste: true, // Pasting in party columns is not supported yet, because it requires a DB lookup.
      valueFormatter: (value: ValueFormatterParams<RowDatum, T>): string => {
        if (value.value == null) {
          return "";
        }
        if (Array.isArray(value.value)) {
          const parties = value.value as UserFieldPluginFrontendValue;
          return parties.map((party) => getPartyFullName(party)).join(", ");
        }
        return String(value.value);
      },
      valueParser: (params: ValueParserParams<RowDatum, T>) => {
        // TODOTABLES (clewis): Make this type-safe. Seems like valueParser is supposed to return a
        // string, but I want it to return a list of selected values.
        //
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        return params.newValue as any; // eslint-disable-line @typescript-eslint/no-unsafe-return
      },
    };
  }
}
